import { type Conversation } from "shared/apiClient";
import { AssessmentLights } from "shared/types";
import { getText, interpolate } from "shared/lib";

import { useCouncilLocalisation } from "shared/hooks";

import { ExternalLinkIcon } from "shared/components";

export interface MPProps {
  assessmentLights: string;
  conversation: Conversation | undefined | null;
}

export function MP({ assessmentLights, conversation }: MPProps) {
  const { getLocalisationEntry } = useCouncilLocalisation(conversation);

  const renderRequestCertificateOfTitle = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Request a Certificate of Title")}
        </h3>

        <p className="text-bodymd mb-2">
          {getText(
            "This tool doesn't account for covenants (restrictions), caveats and easements, which can impact what you can and can't do on a property. A Certificate of Title can include this information, which you can request online.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href={getLocalisationEntry("councilApplyForCertificateOfTitleLink")}
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Request a Certificate of Title")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderCheckBuildingPermitRequirement = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Check if you need a building permit")}
        </h3>

        <p className="text-bodymd mb-2">
          {getText(
            "Building permits are issued separately from planning permits and are required to ensure construction work complies with national building regulations.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.mornpen.vic.gov.au/Building-Planning/Building/Building-Permits"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Check if you need a building permit")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderRequestWrittenPlanningAdvice = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Apply for written planning advice")}
        </h3>

        <p className="text-bodymd mb-2">
          {getText(
            "Your builder may request confirmation from Council that a planning permit isn't required before they start work. To get this, one of our planners needs to review your proposal and provide written planning advice.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.mornpen.vic.gov.au/Building-Planning/Planning/General-Planning-Information/General-Planning-Enquiry"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Apply for written planning advice")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderPrepareForWhatsAhead = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Prepare for what's ahead")}
        </h3>

        <p className="text-bodymd mb-2">
          {getText(
            "See what else you might need to do before you apply, what to expect when you lodge, and how your application will be assessed.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.mornpen.vic.gov.au/Building-Planning/Planning/Planning-Application-Process"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Steps in the planning process")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderPrepareToApplyForPermit = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Get ready to apply for a planning permit")}
        </h3>

        <p className="text-bodymd mb-2">
          {getText(
            "Getting your application right the first time is the best way to avoid delays.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.mornpen.vic.gov.au/Building-Planning/Planning/Planning-Application-Process"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Apply for a planning permit")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderDiscussWithPlanners = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Discuss your proposal with a planner")}
        </h3>

        <p className="text-bodymd mb-2">
          {interpolate(
            "Have questions? Contact us on {{phoneNumber}} or email {{emailLink}} with your report ID:",
            {
              emailLink: (
                <a
                  className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
                  href="mailto:planning@mornpen.vic.gov.au"
                  rel="noreferrer"
                  tabIndex={0}
                  target="_blank"
                >
                  planning@mornpen.vic.gov.au
                </a>
              ),
              phoneNumber: getLocalisationEntry("councilPhoneNumber"),
            },
          )}
        </p>
        <div className="border-interactiveBorder bg-interfaceWhite text-textPrimary text-bodylg w-min text-nowrap rounded border px-2 py-1.5 font-medium">
          {conversation?.identifier?.toUpperCase() || ""}
        </div>
      </div>
    );
  };

  switch (assessmentLights) {
    case AssessmentLights.NoPermitRequired:
      return (
        <>
          {renderRequestCertificateOfTitle()}
          {renderCheckBuildingPermitRequirement()}
          {renderRequestWrittenPlanningAdvice()}
          {renderDiscussWithPlanners()}
        </>
      );
    case AssessmentLights.MoreInformationRequired:
      return (
        <>
          {renderRequestCertificateOfTitle()}
          {renderDiscussWithPlanners()}
        </>
      );
    case AssessmentLights.RequiresPermit:
      return (
        <>
          {renderRequestCertificateOfTitle()}
          {renderPrepareForWhatsAhead()}
          {renderPrepareToApplyForPermit()}
          {renderDiscussWithPlanners()}
        </>
      );
    case AssessmentLights.Prohibited:
      return <>{renderDiscussWithPlanners()}</>;
  }
}
